import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Check, Pencil, X } from 'lucide-react';

export interface SettingsCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  isEditing: boolean;
  isSaving: boolean;
  onEditToggle: () => void;
  onSave: () => void;
  children: React.ReactNode;
}

export const SettingsCard = ({
  title,
  description,
  icon,
  isEditing,
  isSaving,
  onEditToggle,
  onSave,
  children,
}: SettingsCardProps) => {
  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: '#ffffff',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '6px',
        boxShadow: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#ffffff',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          {/* Title & Icon */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '6px',
                bgcolor: 'primary.50',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: '1px solid',
                borderColor: 'primary.100',
                '& svg': {
                  width: 20,
                  height: 20,
                },
              }}
            >
              {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'text.primary',
                  lineHeight: 1.3,
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  mt: 0.25,
                }}
              >
                {description}
              </Typography>
            </Box>
          </Stack>

          {/* Action Buttons */}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
            {isEditing ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onEditToggle}
                  disabled={isSaving}
                  startIcon={<X size={15} />}
                  sx={{
                    minHeight: 32,
                    px: 1.75,
                    borderRadius: '6px',
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={onSave}
                  disabled={isSaving}
                  startIcon={
                    isSaving ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <Check size={15} />
                    )
                  }
                  sx={{
                    minHeight: 32,
                    px: 2,
                    borderRadius: '6px',
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                size="small"
                onClick={onEditToggle}
                startIcon={<Pencil size={15} />}
                sx={{
                  minHeight: 32,
                  px: 1.75,
                  borderRadius: '6px',
                  fontSize: '0.8125rem',
                  textTransform: 'none',
                }}
              >
                Edit
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Content */}
      <CardContent
        sx={{
          p: { xs: 2.5, sm: 3 },
          '&:last-child': {
            pb: { xs: 2.5, sm: 3 },
          },
        }}
      >
        <Stack spacing={3}>
          {children}
        </Stack>
      </CardContent>
    </Card>
  );
};
